/**
 * Sticky Add to Cart Experiment Logic
 * Handles visibility of the sticky bar and instant two-way variant synchronization between
 * the main product page and the sticky add to cart component.
 * Automatically hides the sticky bar when the user scrolls into the footer.
 */

(function initStickyAddToCartModule() {
  'use strict';

  const SELECTORS = {
    root: '[data-sticky-atc-root]',
    image: '[data-sticky-image]',
    price: '[data-sticky-price]',
    variantSelect: '[data-sticky-variant-select]',
    variantText: '[data-sticky-variant-text]',
    form: '[data-sticky-form]',
    button: '[data-sticky-button]',
    mainAtcButton: '[data-main-atc-button]',
    mainVariantPicker: '[data-main-variant-picker], [data-template-product-match="true"], variant-picker, swatches-variant-picker-component',
    mainPriceContainer: '[ref="priceContainer"]',
    mainProductForm: 'product-form-component form, form[data-type="add-to-cart-form"], form[action*="/cart/add"]',
    footer: '[data-footer-root], .footer-content, footer, [id*="footer"], #footer-group'
  };

  const rootElement = document.querySelector(SELECTORS.root);
  if (!rootElement) {
    return;
  }

  const domElements = {
    root: rootElement,
    image: rootElement.querySelector(SELECTORS.image),
    price: rootElement.querySelector(SELECTORS.price),
    variantSelect: rootElement.querySelector(SELECTORS.variantSelect),
    variantText: rootElement.querySelector(SELECTORS.variantText),
    form: rootElement.querySelector(SELECTORS.form),
    button: rootElement.querySelector(SELECTORS.button),
    mainAtcButton: document.querySelector(SELECTORS.mainAtcButton) || document.querySelector('button[name="add"]'),
    mainVariantPicker: document.querySelector(SELECTORS.mainVariantPicker),
    footer: document.querySelector(SELECTORS.footer)
  };

  if (!domElements.mainAtcButton) {
    console.warn('Sticky ATC: Main Add to Cart button not found.');
    return;
  }

  const mainProductId = rootElement.dataset.productId || '';

  let intersectionObserver = null;
  let isSyncingVariant = false;
  let isMainAtcScrolledPast = false;
  let isFooterVisible = false;

  /**
   * Evaluates scroll states and updates the sticky bar visibility class.
   */
  function updateStickyVisibility() {
    const shouldBeVisible = isMainAtcScrolledPast && !isFooterVisible;
    if (shouldBeVisible) {
      domElements.root.classList.add('is-visible');
    } else {
      domElements.root.classList.remove('is-visible');
    }
  }

  /**
   * Returns the product forms that belong to the product this sticky bar is for.
   * Scoped by product id so forms for other products on the page - most notably the cart drawer's
   * recommended products - are never rewritten with this product's variant.
   * @returns {HTMLElement[]}
   */
  function getMainProductForms() {
    const forms = Array.from(document.querySelectorAll('product-form-component'));

    if (mainProductId) {
      const matching = forms.filter((form) => form.dataset.productId === mainProductId);
      if (matching.length) {
        return matching;
      }
    }

    return forms.filter((form) => form.closest('main'));
  }

  /**
   * Updates the main product form hidden ID inputs so the correct variant is submitted.
   * @param {string|number} variantId
   * @param {boolean} [isAvailable] - Availability of the variant. When omitted the add to cart
   *   button's enabled state is left untouched so the theme stays in control of it.
   */
  function updateAllProductFormIds(variantId, isAvailable) {
    if (!variantId) {
      return;
    }
    try {
      const targetId = String(variantId);

      getMainProductForms().forEach((component) => {
        const input = component.querySelector('input[name="id"]');
        if (input instanceof HTMLInputElement) {
          input.value = targetId;
        }

        if (component.refs && component.refs.variantId) {
          component.refs.variantId.value = targetId;
        }

        const addToCartContainer = component.refs && component.refs.addToCartButtonContainer;
        if (!addToCartContainer) {
          return;
        }

        if (isAvailable === true) {
          addToCartContainer.enable();
        } else if (isAvailable === false) {
          addToCartContainer.disable();
        }
      });
    } catch (error) {
      console.error('Sticky ATC: Failed to update product form IDs', error);
    }
  }

  /**
   * Updates the sticky UI elements instantly with the provided variant details.
   * @param {Object} variantData
   * @param {string|number} variantData.id
   * @param {string} [variantData.title]
   * @param {string} [variantData.priceFormatted]
   * @param {boolean} [variantData.isAvailable]
   * @param {string} [variantData.imageUrl]
   */
  function updateStickyUI(variantData) {
    if (!variantData) {
      return;
    }

    const { id, title, priceFormatted, isAvailable, imageUrl } = variantData;

    // 1. Update dropdown value if different
    if (domElements.variantSelect && id) {
      const targetValue = String(id);
      if (domElements.variantSelect.value !== targetValue) {
        domElements.variantSelect.value = targetValue;
      }
    }

    // 2. Update visible variant label text
    if (domElements.variantText) {
      if (domElements.variantSelect && domElements.variantSelect.options) {
        const selectedOption = domElements.variantSelect.options[domElements.variantSelect.selectedIndex];
        if (selectedOption) {
          domElements.variantText.textContent = selectedOption.textContent.trim();
        } else if (title) {
          domElements.variantText.textContent = `Size: ${title}`;
        }
      } else if (title) {
        domElements.variantText.textContent = `Size: ${title}`;
      }
    }

    // 3. Update Price
    if (domElements.price && priceFormatted) {
      domElements.price.textContent = priceFormatted;
    }

    // 4. Update Image
    if (domElements.image && imageUrl) {
      domElements.image.src = imageUrl;
    }

    // 5. Update Button State
    if (domElements.button && typeof isAvailable === 'boolean') {
      domElements.button.disabled = !isAvailable;
      const addToCartText = domElements.button.dataset.addToCartText || 'Add To Cart';
      const soldOutText = domElements.button.dataset.soldOutText || 'Sold Out';
      domElements.button.textContent = isAvailable ? addToCartText : soldOutText;
    }
  }

  /**
   * Instantly handles user selection change from the main variant picker (zero network latency).
   * @param {Event} event
   */
  function handleMainVariantChange(event) {
    if (isSyncingVariant) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const mainPicker = target.closest(SELECTORS.mainVariantPicker);
    if (!mainPicker) {
      return;
    }

    try {
      const targetVariantId = target.dataset.variantId;
      const targetValue = target instanceof HTMLSelectElement ? target.value : target.getAttribute('value');

      if (!domElements.variantSelect || !domElements.variantSelect.options) {
        return;
      }

      let matchingOption = null;

      if (targetVariantId) {
        matchingOption = Array.from(domElements.variantSelect.options).find(
          (opt) => String(opt.value) === String(targetVariantId)
        );
      }

      if (!matchingOption && targetValue) {
        matchingOption = Array.from(domElements.variantSelect.options).find((opt) => {
          const title = opt.dataset.variantTitle || '';
          const opt1 = opt.getAttribute('data-option-1') || opt.getAttribute('data-option1') || '';
          const opt2 = opt.getAttribute('data-option-2') || opt.getAttribute('data-option2') || '';
          const opt3 = opt.getAttribute('data-option-3') || opt.getAttribute('data-option3') || '';
          return (
            title === targetValue ||
            opt1 === targetValue ||
            opt2 === targetValue ||
            opt3 === targetValue ||
            opt.textContent.trim().endsWith(targetValue)
          );
        });
      }

      if (matchingOption) {
        updateStickyUI({
          id: matchingOption.value,
          title: matchingOption.dataset.variantTitle || '',
          priceFormatted: matchingOption.dataset.variantPrice || '',
          isAvailable: matchingOption.dataset.variantAvailable !== 'false' && !matchingOption.disabled,
          imageUrl: matchingOption.dataset.variantImage || ''
        });

        // Ensure main form ID is also synced. The variant isn't confirmed by the theme's fetch
        // yet, so the main button's state is deliberately left alone here.
        updateAllProductFormIds(matchingOption.value);
      }
    } catch (error) {
      console.error('Sticky ATC: Error handling main variant instant change', error);
    }
  }

  /**
   * Handles incoming variant:update event from the theme / main variant picker after fetch.
   * @param {CustomEvent} event
   */
  function handleVariantUpdate(event) {
    if (!event || !event.detail || !event.detail.resource) {
      return;
    }

    if (isSyncingVariant) {
      return;
    }

    try {
      const variant = event.detail.resource;
      const variantId = variant.id;
      if (!variantId) {
        return;
      }

      let priceFormatted = '';
      let imageUrl = '';
      const isAvailable = Boolean(variant.available);

      // Extract price from event data HTML if available
      const eventDoc = event.detail.data && event.detail.data.html;
      if (eventDoc) {
        const priceElement = eventDoc.querySelector(SELECTORS.mainPriceContainer);
        if (priceElement) {
          // Strip non-price content (discount badges, unit price, quick view button) so only
          // the actual price text is read - the sticky bar isn't meant to echo the badge copy.
          const priceClone = priceElement.cloneNode(true);
          priceClone.querySelectorAll('.cp-card-badge-wrap, .cp-pdp-badge-wrap, .unit-price, .as_us_qv-btn').forEach((el) => el.remove());
          priceFormatted = priceClone.textContent.trim();
        }
      }

      // Check matching option in sticky select
      if (domElements.variantSelect && domElements.variantSelect.options) {
        const matchingOption = Array.from(domElements.variantSelect.options).find(
          (opt) => String(opt.value) === String(variantId)
        );
        if (matchingOption) {
          if (!priceFormatted && matchingOption.dataset.variantPrice) {
            priceFormatted = matchingOption.dataset.variantPrice;
          }
          if (matchingOption.dataset.variantImage) {
            imageUrl = matchingOption.dataset.variantImage;
          }
        }
      }

      if (!imageUrl && variant.featured_media && variant.featured_media.preview_image) {
        imageUrl = variant.featured_media.preview_image.src;
      } else if (!imageUrl && variant.featured_image) {
        imageUrl = typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image.src;
      }

      updateStickyUI({
        id: variantId,
        title: variant.title,
        priceFormatted,
        isAvailable,
        imageUrl
      });

      updateAllProductFormIds(variantId, isAvailable);
    } catch (error) {
      console.error('Sticky ATC: Failed to handle variant update event', error);
    }
  }

  /**
   * Retrieves the main variant picker element dynamically.
   * @returns {HTMLElement|null}
   */
  function getMainVariantPicker() {
    if (domElements.mainVariantPicker && domElements.mainVariantPicker.isConnected) {
      return domElements.mainVariantPicker;
    }
    domElements.mainVariantPicker =
      document.querySelector(SELECTORS.mainVariantPicker) || document.querySelector('variant-picker');
    return domElements.mainVariantPicker;
  }

  /**
   * Synchronizes variant selection from the sticky dropdown to the main product page instantly.
   */
  function handleVariantSelectChange() {
    if (!domElements.variantSelect) {
      return;
    }

    try {
      const selectedVariantId = domElements.variantSelect.value;
      if (!selectedVariantId) {
        return;
      }

      const selectedOption = domElements.variantSelect.options[domElements.variantSelect.selectedIndex];
      if (selectedOption) {
        const priceFormatted = selectedOption.dataset.variantPrice || '';
        const imageUrl = selectedOption.dataset.variantImage || '';
        const isAvailable = selectedOption.dataset.variantAvailable !== 'false' && !selectedOption.disabled;

        // 1. Instant UI update on sticky bar
        updateStickyUI({
          id: selectedVariantId,
          title: selectedOption.dataset.variantTitle || '',
          priceFormatted,
          isAvailable,
          imageUrl
        });
      }

      // 2. Immediately sync all main product form ID inputs
      updateAllProductFormIds(selectedVariantId);

      const mainPicker = getMainVariantPicker();
      if (!mainPicker) {
        return;
      }

      isSyncingVariant = true;

      // 3. Find and activate matching option in main variant picker
      let hasTriggered = false;

      // Match by option values (option1, option2, option3) or title
      if (selectedOption) {
        const optionValues = [
          selectedOption.getAttribute('data-option-1') || selectedOption.getAttribute('data-option1') || selectedOption.dataset.variantTitle,
          selectedOption.getAttribute('data-option-2') || selectedOption.getAttribute('data-option2'),
          selectedOption.getAttribute('data-option-3') || selectedOption.getAttribute('data-option3')
        ].filter(Boolean);

        optionValues.forEach((val) => {
          if (!val) return;
          const radio = mainPicker.querySelector(`input[type="radio"][value="${CSS.escape(val)}"]`);
          if (radio instanceof HTMLInputElement) {
            radio.click();
            hasTriggered = true;
          } else {
            const selectOption = mainPicker.querySelector(`select option[value="${CSS.escape(val)}"]`);
            if (selectOption && selectOption.parentElement instanceof HTMLSelectElement) {
              selectOption.parentElement.value = val;
              selectOption.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
              hasTriggered = true;
            }
          }
        });
      }

      // Fallback: match by data-variant-id
      if (!hasTriggered) {
        const targetRadio = mainPicker.querySelector(`input[data-variant-id="${selectedVariantId}"]`);
        if (targetRadio instanceof HTMLInputElement) {
          targetRadio.click();
        } else {
          const targetOption = mainPicker.querySelector(`option[data-variant-id="${selectedVariantId}"]`);
          if (targetOption && targetOption.parentElement instanceof HTMLSelectElement) {
            targetOption.parentElement.value = targetOption.value;
            targetOption.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      // Reset sync flag on next animation frame
      requestAnimationFrame(() => {
        isSyncingVariant = false;
      });
    } catch (error) {
      isSyncingVariant = false;
      console.error('Sticky ATC: Failed to sync variant to main picker', error);
    }
  }

  /**
   * Handles sticky form submit by guaranteeing the correct variant ID is submitted.
   * @param {Event} event
   */
  function handleFormSubmit(event) {
    event.preventDefault();

    try {
      const selectedVariantId = domElements.variantSelect ? domElements.variantSelect.value : null;
      const selectedOption =
        domElements.variantSelect && domElements.variantSelect.options
          ? domElements.variantSelect.options[domElements.variantSelect.selectedIndex]
          : null;
      const isAvailable = selectedOption
        ? selectedOption.dataset.variantAvailable !== 'false' && !selectedOption.disabled
        : true;

      // Never add a sold out variant, even if the button was somehow submitted.
      if (!isAvailable) {
        return;
      }

      // 1. Force update all product form inputs to the selected variant ID
      if (selectedVariantId) {
        updateAllProductFormIds(selectedVariantId, true);
      }

      // 2. Re-query main ATC button
      if (!domElements.mainAtcButton || !domElements.mainAtcButton.isConnected) {
        domElements.mainAtcButton =
          document.querySelector(SELECTORS.mainAtcButton) || document.querySelector('button[name="add"]');
      }

      // 3. Re-enable main ATC button if it was disabled
      if (domElements.mainAtcButton) {
        domElements.mainAtcButton.disabled = false;
        domElements.mainAtcButton.click();
      } else if (domElements.form) {
        domElements.form.submit();
      }
    } catch (error) {
      console.error('Sticky ATC: Failed to submit form', error);
      if (domElements.form) {
        domElements.form.submit();
      }
    }
  }

  /**
   * Intersection observer callback to toggle sticky bar visibility based on main ATC and footer positions.
   * @param {IntersectionObserverEntry[]} entries
   */
  function handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === domElements.mainAtcButton) {
        if (entry.isIntersecting) {
          isMainAtcScrolledPast = false;
        } else {
          isMainAtcScrolledPast = entry.boundingClientRect.top < 0;
        }
      } else if (domElements.footer && (entry.target === domElements.footer || entry.target.contains(domElements.footer) || domElements.footer.contains(entry.target))) {
        isFooterVisible = entry.isIntersecting;
      }
    });

    updateStickyVisibility();
  }

  /**
   * Initializes the intersection observer on the main ATC button and footer.
   */
  function setupObserver() {
    if (!domElements.mainAtcButton) {
      return;
    }

    if (!domElements.footer) {
      domElements.footer = document.querySelector(SELECTORS.footer);
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    intersectionObserver = new IntersectionObserver(handleIntersection, options);
    intersectionObserver.observe(domElements.mainAtcButton);

    if (domElements.footer) {
      intersectionObserver.observe(domElements.footer);
    }
  }

  /**
   * Sets up all necessary event listeners.
   */
  function setupEventListeners() {
    // 1. Listen for instant user input on main variant picker (zero network lag)
    document.addEventListener('change', handleMainVariantChange);

    // 2. Listen for theme variant update event (post-fetch validation)
    document.addEventListener('variant:update', handleVariantUpdate);

    // 3. Listen for changes on sticky variant dropdown
    if (domElements.variantSelect) {
      domElements.variantSelect.addEventListener('change', handleVariantSelectChange);
    }

    // 4. Form submission handler
    if (domElements.form) {
      domElements.form.addEventListener('submit', handleFormSubmit);
    }
  }

  /**
   * Module initializer.
   */
  function init() {
    try {
      setupObserver();
      setupEventListeners();
    } catch (error) {
      console.error('Sticky ATC: Error during initialization:', error);
    }
  }

  init();
})();
